import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Championship, Organizer } from 'modules/overlay-v2/domain/interfaces/game-info/gameInfoMapped';
import { FoulsByPeriod, Goals, Match, Teams } from 'modules/overlay-v2/domain/interfaces/game-info/state';

import { MatchEvent } from 'shared/types/match-event';
import { ShootoutAttempts } from 'shared/types/shootout';
import { emptyShootoutAttempts } from 'shared/utils/shootout';
import { createResetAllReducer, createSyncDataReducer } from './reducers';
import parseInfoEvent from './utils/parse-info.event';
import { resetTeamStats } from './utils/reset-team-stats';

export type GameInfoSliceState = {
  match?: Match;
  teams?: Teams;
  organizer?: Organizer;
  championship?: Championship;
  sportType?: SportType;

  goals: Goals;
  fouls: FoulsByPeriod;
  // Удары серии пенальти хранятся последовательностью, а не парой счётчиков: из неё
  // выводится и счёт серии (селекторы), и цепочка индикаторов на табло. Два счётчика
  // порядок ударов не восстанавливают.
  shootout: { attempts: ShootoutAttempts };

  isLoading: boolean;
  isLoaded: boolean;

  isSynchronization: boolean;
  isSynchronize: boolean;

  parsedEventsId: number[];
  error?: string;
};

const initialState: GameInfoSliceState = {
  isLoaded: false,
  isLoading: false,
  isSynchronization: false,
  isSynchronize: false,
  goals: { first: 0, second: 0 },
  // Объект, а не массив: ключ — номер периода. С массивом запись `fouls[2]` давала
  // разреженный массив, который при передаче в окна оверлея превращался в `{0: null, …}`.
  fouls: {},
  shootout: { attempts: emptyShootoutAttempts() },
  parsedEventsId: [],
  error: undefined,
};

export const gameInfoSlice = createSlice({
  name: 'overlay-v2/gameInfo',
  initialState,
  reducers: {
    setGoals: (state, action: PayloadAction<{ first?: number; second?: number }>) => {
      action.payload.first !== undefined && (state.goals.first = action.payload.first);
      action.payload.second !== undefined && (state.goals.second = action.payload.second);
    },
    setFouls: (state, action: PayloadAction<FoulsByPeriod>) => {
      state.fouls = action.payload;
    },
    setShootout: (state, action: PayloadAction<ShootoutAttempts>) => {
      state.shootout.attempts = action.payload;
    },
    setFoulsOnPeriod: (state, action: PayloadAction<{ first?: number; second?: number; period: number }>) => {
      if (!action.payload.period) return;
      state.fouls[action.payload.period] = {
        first: Math.max(0, action.payload.first ?? state.fouls[action.payload.period]?.first ?? 0),
        second: Math.max(0, action.payload.second ?? state.fouls[action.payload.period]?.second ?? 0),
      };
    },
    parseEvent: (state, action: PayloadAction<{ events: MatchEvent[] }>) => {
      applyEvents(state, action.payload.events);
    },
    /**
     * Полный перебор хронологии заново — ответ на удаление события в приложении.
     *
     * `parseEvent` умеет только прибавлять: id, однажды учтённый в `parsedEventsId`,
     * больше не трогается, и исчезнувший из хронологии гол или удар серии остаётся
     * на табло до ручной синхронизации. Здесь производные счётчики обнуляются и
     * собираются из присланного списка целиком.
     *
     * Одним экшеном, а не парой `resetGameInfo` + `parseEvent`: два диспатча дали бы
     * кадр с обнулённым табло прямо в эфире.
     */
    reparseEvents: (state, action: PayloadAction<{ events: MatchEvent[] }>) => {
      state.goals = { first: 0, second: 0 };
      state.fouls = {};
      state.shootout.attempts = emptyShootoutAttempts();
      state.parsedEventsId = [];
      if (state.teams?.first) state.teams.first = resetTeamStats(state.teams.first);
      if (state.teams?.second) state.teams.second = resetTeamStats(state.teams.second);

      applyEvents(state, action.payload.events);
    },
    setPassedEventsId: (state, action: PayloadAction<number[]>) => {
      state.parsedEventsId = action.payload;
    },
  },
  extraReducers: (builder) => {
    createSyncDataReducer(builder);
    createResetAllReducer(builder);
  },
});

/**
 * Разбор пачки событий: каждое событие учитывается один раз, признак — `parsedEventsId`.
 * Вынесено из редьюсера, чтобы тем же кодом пользовался полный перебор `reparseEvents`.
 */
function applyEvents(state: GameInfoSliceState, events: MatchEvent[]) {
  for (const event of events) {
    if (_.find(state.parsedEventsId, (id) => id === event.id)) continue;
    state.parsedEventsId.push(event.id);
    switch (event.type) {
      case 'GOAL':
      case 'PASS_AND_GOAL':
      case 'PENALTY_GOAL': {
        const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
        if (teamPrefix) {
          state.goals[teamPrefix] += 1;
        }
        if (team && player) {
          team.members = team.members?.map((member) => {
            if (member.id === player.id) {
              member.qtyGoalsMatch += 1;
            }
            return member;
          });
        }
        break;
      }
      case 'AFTER_MATCH_PENALTY_GOAL': {
        const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
        if (teamPrefix) {
          state.shootout.attempts[teamPrefix].push('GOAL');
        }
        if (team && player) {
          team.members = team.members?.map((member) => {
            if (member.id === player.id) {
              member.qtyShootoutGoalsMatch += 1;
            }
            return member;
          });
        }
        break;
      }
      case 'AFTER_MATCH_PENALTY_MISSED': {
        const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
        if (teamPrefix) {
          state.shootout.attempts[teamPrefix].push('MISS');
        }
        if (team && player) {
          team.members = team.members?.map((member) => {
            if (member.id === player.id) {
              member.qtyShootoutMissesMatch += 1;
            }
            return member;
          });
        }
        break;
      }
      case 'OWN_GOAL': {
        const { teamPrefix } = parseInfoEvent(event, state.teams, { teamReverse: true });
        if (teamPrefix) {
          state.goals[teamPrefix] += 1;
        }
        break;
      }
      case 'FOUL': {
        const period = event.period;
        // Пропускаем только это событие: `return` прерывал бы разбор всей пачки,
        // а идущие следом события терялись бы навсегда — их id уже в parsedEventsId.
        if (!period) break;

        const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
        if (team && teamPrefix) {
          if (!state.fouls[period]) {
            state.fouls[period] = { first: 0, second: 0 };
          }
          state.fouls[period][teamPrefix] += 1;
        }
        // Тот же счётчик наполняет `updateMembersStats` при синхронизации:
        // если считать фолы только там, эфир и перезагрузка страницы разойдутся.
        if (player) {
          player.qtyFoulsMatch += 1;
        }
        break;
      }
      case 'RED_CARD': {
        const { player } = parseInfoEvent(event, state.teams);
        if (!player) break;
        player.qtyRedCardsMatch += 1;
        break;
      }
      case 'YELLOW_CARD': {
        const { player } = parseInfoEvent(event, state.teams);
        if (!player) break;
        player.qtyYellowCardsMatch += 1;
        break;
      }
    }
  }
}

export const { setGoals, setFouls, setShootout, parseEvent, reparseEvents, setPassedEventsId, setFoulsOnPeriod } =
  gameInfoSlice.actions;
